// src/prints/HireTotalBill.jsx
import React, { useEffect, useState } from "react";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import { useParams } from "react-router-dom";
import axios from "axios";
import Logo from "../assets/logo.png";
import Swal from "sweetalert2";

/* ================= helpers (same behavior as other pages) ================= */

const pad2 = (n) => String(n).padStart(2, "0");

// dd/mm/yyyy
const formatDMY = (dLike) => {
  const d = new Date(dLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Date -> h:mm AM/PM
const format12h = (d) => {
  if (!d) return "";
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};

// decimal hours (e.g., 10.5) -> "HH:MM"
const decToHHMM = (decimalHrs) => {
  if (decimalHrs === null || decimalHrs === undefined || decimalHrs === "") return "";
  const n = Number(decimalHrs);
  if (Number.isNaN(n)) return String(decimalHrs);
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};

// decimal hours -> AM/PM time-of-day
const decToAMPM = (decimalHrs) => {
  const hhmm = decToHHMM(decimalHrs);
  if (!hhmm.includes(":")) return "";
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  return format12h(t);
};

// minutes -> decimal hours
const minutesToDecimalHours = (mins) => {
  const m = Number(mins || 0);
  return m / 60;
};

// Working total formatter used everywhere
const formatWorkingTotal = (hoursDecimal) => {
  if (hoursDecimal === null || hoursDecimal === undefined) return "";
  const n = Number(hoursDecimal);
  if (Number.isNaN(n)) return "";

  if (n < 24) {
    const s = Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(1))}`;
    return `${s} hours`;
  }
  const days = Math.floor(n / 24);
  const rem = n - days * 24;
  if (Math.abs(rem) < 1e-6) return `${days} day${days > 1 ? "s" : ""}`;
  const remStr = Number.isInteger(rem) ? `${rem}` : `${parseFloat(rem.toFixed(1))}`;
  return `${days} day${days > 1 ? "s" : ""} ${remStr}h`;
};

// tiny print-friendly chip + stack
const chipStyle = (highlight) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.6,
  background: highlight ? "#fff7ed" : "#eef2ff",
  color: highlight ? "#9a3412" : "#3730a3",
});
const cellStack = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
};
/* ========================================================================= */

const API_BASE =
  import.meta.env?.VITE_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  "https://rfs2.infraconsoft.net.in/api";

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const HireTotalBill = () => {
  useAuthRedirect();
  const { id, dcNo, branch, vcode, billNo } = useParams();

  const [details, setVehicleDetails] = useState("");
  const [header, setHeader] = useState({});
  const [data, setData] = useState([]);
  const [vehicle, setVehicle] = useState("");
  const [km, setKm] = useState("");
  const [lastDate, setLastDate] = useState(null);

  // ---- Site / Company info for header ----
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,
    address: "",
    email: "",
    phone: "",
    _ts: 0,
  });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/siteinfo`);
        const s = res?.data || {};
        // ensure absolute logo
        let logo = s.logo || null;
        if (logo && !/^https?:\/\//i.test(logo)) {
          if (!logo.startsWith("/")) logo = `/${logo}`;
          logo = `${API_ORIGIN}${logo}`;
        }
        if (!ignore) {
          setSite({
            ok: !!s.ok,
            name: s.name || "Infraconsoft",
            logo,
            address: s.address || "",
            email: s.email || "",
            phone: s.phone || "",
            _ts: Date.now(),
          });
        }
      } catch (e) {
        // keep defaults
        console.error("Failed to load /siteinfo", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    const fetchVehicleDataWithProgress = async () => {
      let progress = 0;

      const updateProgress = (val) => {
        const bar = document.getElementById("progress-bar");
        const text = document.getElementById("progress-text");
        if (bar) bar.style.width = `${val}%`;
        if (text) text.innerText = `Loading... ${val}%`;
      };

      Swal.fire({
        title: "Loading Vehicle & Billing Details",
        html: `
          <div id="progress-container" style="width: 100%; background: #e0e0e0; border-radius: 5px;">
            <div id="progress-bar" style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); border-radius: 5px;"></div>
          </div>
          <p id="progress-text" style="margin-top: 10px; font-size: 14px;">Loading... 0%</p>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => updateProgress(0),
      });

      try {
        // Step 1: Vehicle details (basis, price, etc.)
        const response = await axios.get(`${API_BASE}/vehicleBasis?vcode=${vcode}`);
        progress = 50;
        updateProgress(progress);

        if (response.data.vBasis && response.data.vBasis.length > 0) {
          const basisData = response.data.vBasis[0];
          const fld_vbasis = basisData.fld_vbasis || "";
          const fld_vno = basisData.fld_vehno || "";
          const fld_vtype = basisData.fld_vtype || "";
          const fld_date = basisData.fld_date;
          const fld_vprice = basisData.fld_vprice;

          setVehicle({
            fld_vbasis,
            fld_vno,
            fld_vtype,
            fld_date,
            fld_vprice,
          });

          let Km = "";
          if (fld_vbasis === "Hour") Km = "Hour";
          else if (fld_vbasis === "Kmhour") Km = "Kmhour";
          else if (fld_vbasis === "Km") Km = "Km";
          else if (fld_vbasis === "Ton" || fld_vbasis === "Month") Km = "Km";
          setKm(Km);
        }

        // Step 2: Bill bottom details
        const res2 = await axios.get(`${API_BASE}/billReport_bottomDetails?billNo=${billNo}`);
        progress = 100;
        updateProgress(progress);

        const bottomDetails = res2.data.bottomDetails || [];
        setData(bottomDetails);

        if (bottomDetails.length > 0) {
          const sorted = [...bottomDetails].sort(
            (a, b) => new Date(a.fld_vdate) - new Date(b.fld_vdate)
          );
          setLastDate(sorted[sorted.length - 1].fld_vdate);
        } else {
          setLastDate(null);
        }

        setTimeout(() => Swal.close(), 400);
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch vehicle or billing details.",
        });
      }
    };

    if (vcode && billNo) {
      fetchVehicleDataWithProgress();
    }
  }, [vcode, billNo]);

  const handlePrint = () => window.print();

  // header logo with fallback
  const logoSrc = site.logo ? `${site.logo}${site._ts ? `?v=${site._ts}` : ""}` : Logo;

  return (
    <div className="delivery-note-container">
      {/* Header */}
      <div className="header">
        <div className="logo">
          <img src={logoSrc} alt={`${site.name} Logo`} />
        </div>
        <div className="company-info">
          <h2 style={{ textTransform: "uppercase" }}>{site.name || "Infraconsoft"}</h2>
          <p>
            {site.address && <span>{site.address}.<br /></span>}
            {site.phone && <span>Ph: {site.phone}</span>}
            {(site.phone && site.email) && <span> | </span>}
            {site.email && (
              <span>
                <strong>E-mail: {site.email}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Vehicle meta */}
      <div className="delivery-details">
        <div>
          <p>
            <strong>Vehicle No:</strong> {vehicle?.fld_vno}
          </p>
          <p>
            <strong>Vehicle Name:</strong> {vehicle?.fld_vtype}
          </p>
          <p>
            <strong>Period:</strong>
            {vehicle?.fld_date && formatDMY(vehicle.fld_date)} TO{" "}
            {lastDate && formatDMY(lastDate)}
          </p>
        </div>
      </div>

      {/* Items */}
      <table className="items-table">
        <thead>
          <tr>
            <th>Date</th>

            {(vehicle?.fld_vbasis === "Hour" || vehicle?.fld_vbasis === "Km") && (
              <>
                <th>Starting Hours</th>
                <th>Closing Hours</th>
              </>
            )}

            {vehicle?.fld_vbasis === "Km" && <th>Breaking Hours</th>}

            {(vehicle?.fld_vbasis === "Hour" || vehicle?.fld_vbasis === "Km") && (
              <>
                <th>Working Hours</th>
                <th>Cumulative Hours</th>
              </>
            )}

            <th>Starting KM</th>
            <th>Closing KM</th>
            <th>Working KM</th>
            <th>Cumulative KM</th>
            <th>No Of Loads</th>
            <th>Rent</th>

            {vehicle?.fld_vbasis === "Ton" && <th>Tons</th>}

            {(vehicle?.fld_vbasis === "Hour" ||
              vehicle?.fld_vbasis === "Km" ||
              vehicle?.fld_vbasis === "Ton") && (
              <>
                <th>Amount</th>
                <th>Cum Amt</th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {(() => {
            let cumKm = 0;       // cumulative KM in table
            let cumMins = 0;     // cumulative minutes for hours-based columns
            let cumAmt = 0;

            return data.length > 0 ? (
              data.map((item, index) => {
                // Date display
                const dateStr = item?.fld_vdate ? formatDMY(item.fld_vdate) : "";

                // KM math (as you had)
                const stkm = parseFloat(item.fld_stkm) || 0;
                const endkm = parseFloat(item.fld_endkm) || 0;
                const wkm = Math.round(endkm - stkm);
                cumKm += wkm;

                // Hours math: your payload’s fld_wohrs is minutes
                const minsThisRow = parseFloat(item.fld_wohrs) || 0;
                cumMins += minsThisRow;

                const rowHoursDec = minutesToDecimalHours(minsThisRow);
                const cumHoursDec = minutesToDecimalHours(cumMins);

                const workingPretty = formatWorkingTotal(rowHoursDec);
                const cumulativePretty = formatWorkingTotal(cumHoursDec);

                // AM/PM Start/End (decimal-of-day to AM/PM)
                const startAMPM = decToAMPM(item.fld_sthrs);
                const endAMPM = decToAMPM(item.fld_endhrs);

                // Multi-day highlight if this row >= 24h
                const isMultiDay = rowHoursDec >= 24;

                // Amount (kept as you wrote)
                const tons = parseFloat(item.fld_tons) || 0;
                const price = parseFloat(vehicle?.fld_vprice) || 0;

                let amount = 0;
                if (vehicle?.fld_vbasis === "Hour") {
                  // you used per-minute billing here
                  amount = minsThisRow * price;
                } else if (vehicle?.fld_vbasis === "Km") {
                  // your PHP-matching computation
                  const tothour = Math.floor(cumMins / 60);
                  const min = cumMins % 60;
                  const totalmin = Math.round(min * 1.666); // your original factor
                  const tothour21 = parseFloat(
                    `${tothour}.${totalmin.toString().padStart(2, "0")}`
                  );
                  amount = tothour21 * price;
                } else if (vehicle?.fld_vbasis === "Ton" || vehicle?.fld_vbasis === "Month") {
                  amount = wkm * tons * price;
                }
                cumAmt += amount;

                return (
                  <tr key={index}>
                    {/* Date */}
                    <td>
                      <div style={cellStack}>
                        <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                      </div>
                    </td>

                    {/* Start / End */}
                    {(vehicle?.fld_vbasis === "Hour" || vehicle?.fld_vbasis === "Km") && (
                      <>
                        <td>
                          <div style={cellStack}>
                            <span style={{ fontWeight: 700 }}>{startAMPM || "—"}</span>
                          </div>
                        </td>
                        <td>
                          <div style={cellStack}>
                            <span style={{ fontWeight: 700 }}>{endAMPM || "—"}</span>
                          </div>
                        </td>
                      </>
                    )}

                    {/* Break time (for Km) */}
                    {vehicle?.fld_vbasis === "Km" && (
                      <td>
                        {pad2(item?.fld_brehrs ?? 0)}:{pad2(item?.fld_bremin ?? 0)}
                      </td>
                    )}

                    {/* Working / Cumulative Hours (pretty) */}
                    {(vehicle?.fld_vbasis === "Hour" || vehicle?.fld_vbasis === "Km") && (
                      <>
                        <td>{workingPretty || "—"}</td>
                        <td>{cumulativePretty || "—"}</td>
                      </>
                    )}

                    {/* KM columns (as-is) */}
                    <td>{stkm}</td>
                    <td>{endkm}</td>
                    <td>{wkm}</td>
                    <td>{cumKm}</td>

                    <td>{item.fld_noloads}</td>
                    <td>{vehicle?.fld_vprice}</td>

                    {vehicle?.fld_vbasis === "Ton" && <td>{item.fld_tons}</td>}

                    {/* Amounts (kept) */}
                    {(vehicle?.fld_vbasis === "Hour" ||
                      vehicle?.fld_vbasis === "Km" ||
                      vehicle?.fld_vbasis === "Ton") && (
                      <>
                        <td>{amount.toFixed(2)}</td>
                        <td>{cumAmt.toFixed(2)}</td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9">No items</td>
              </tr>
            );
          })()}
        </tbody>
      </table>

      <br />
      <br />
      <br />
      <br />
      <br />

      {/* Signature strip */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          textAlign: "center",
          width: "100%",
          gap: "10px",
        }}
      >
        <div style={{ flex: 1 }}>
          <strong>PREPARED BY</strong>
        </div>
        <div style={{ flex: 1 }}>
          <strong>CHECKED BY</strong>
        </div>
        <div style={{ flex: 1 }}>
          <strong>SITE INCHARGE</strong>
        </div>
        <div style={{ flex: 1 }}>
          <strong>APPROVED BY</strong>
        </div>
      </div>

      {/* Print button */}
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: "#4a90e2",
            color: "white",
            border: "none",
            padding: "12px 20px",
            fontSize: "16px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background 0.3s ease-in-out",
            marginTop: "20px",
          }}
        >
          Print
        </button>
      </div>
    </div>
  );
};

export default HireTotalBill;
